import React, { useEffect, useMemo, useRef, useState } from "react";
import { RankingParadoEmpleados } from "../components/RankingParadoEmpleados";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, formatMoney, formatMoney2, formatNumber, formatPct } from "../lib/api";
import { BannerFarmanaliza } from "../components/BannerFarmanaliza";
import { RatiosEvolucion } from "../components/RatiosEvolucion";
import { uploadSales } from "../lib/salesUpload";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";
import { VentasComparativa } from "../components/VentasComparativa";
import { Hist, HistLabel } from "../components/HistRef";
import { MargenCard } from "../components/MargenCard";
import { TopAconsejadores } from "../components/TopAconsejadores";
import { ProductosEstrella } from "../components/ProductosEstrella";
import { toast } from "sonner";

const ratio2 = (v) => (v ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num1 = (v) => (v ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numInt = (v) => formatNumber(Math.round(v || 0));
import {
  UploadCloud, Loader2, Trash2, TrendingUp, Receipt, Pill, Sparkles, Clock,
  CreditCard, Package, Target, ChevronDown, ChevronUp, BarChart3, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, ComposedChart,
} from "recharts";

const KIND_LABEL = {
  farmatic_diario: "Diario Farmatic",
  nixfarma: "Listado Nixfarma",
  resumen: "Resumen por producto",
};

function Kpi({ label, value, sub, icon: Icon, accent = "slate", testid, hist }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        {Icon && <Icon size={16} className={`text-${accent}-400`} />}
      </div>
      <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      {hist && <div className="mt-1.5 pt-1.5 border-t border-slate-100">{hist}</div>}
    </div>
  );
}

function UploadCard({ onDone }) {
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Elige el fichero de ventas"); return; }
    setBusy(true); setErr("");
    const fd = new FormData();
    fd.append("file", file);
    if (tipo) fd.append("tipo", tipo);
    try {
      const data = await uploadSales(fd, setSecs);
      setFile(null); setTipo(""); setOpen(false);
      onDone?.(data.id || data.meses?.[data.meses.length - 1]?.id);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message || "No se pudo subir el fichero");
    } finally { setBusy(false); setSecs(0); }
  };

  return (
    <div className="card mb-6 overflow-hidden" data-testid="sales-upload-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-slate-50 transition"
              data-testid="toggle-sales-upload">
        <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center"><UploadCloud size={17} /></div>
        <div className="flex-1">
          <div className="font-display font-bold">Subir ventas del mes</div>
          <div className="text-xs text-slate-500">Diario de ventas de Farmatic, listado de Nixfarma o resumen por producto · el periodo se detecta solo</div>
        </div>
        {open ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
      </button>
      {open && (
        <form onSubmit={submit} className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-3" data-testid="sales-upload-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Fichero Excel</label>
              <div className="mt-1 border-2 border-dashed border-slate-300 rounded-lg px-3 py-2 cursor-pointer hover:border-slate-500 transition text-sm truncate"
                   onClick={() => inputRef.current?.click()} data-testid="sales-file-drop">
                <input ref={inputRef} type="file" accept=".xlsx,.xls,.xlsm" className="hidden"
                       onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="sales-file-input" />
                {file ? <span className="text-emerald-700 font-semibold">{file.name}</span> : <span className="text-slate-500">Elegir archivo…</span>}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Tipo (solo resumen por producto)</label>
              <select className="input w-full mt-1" value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="sales-tipo-select">
                <option value="">Detectar automáticamente</option>
                <option value="libre">Resumen de venta libre</option>
                <option value="receta">Resumen de venta receta</option>
              </select>
            </div>
          </div>
          {err && <div className="text-xs text-red-700" data-testid="sales-upload-error">{err}</div>}
          <div className="flex items-center gap-3">
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="sales-upload-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Procesando{secs ? ` · ${secs} s` : "…"}</> : <><UploadCloud size={14} /> Subir y analizar</>}
            </button>
            <span className="text-xs text-slate-500">El mes se deduce de las fechas del fichero. Se cruza con BOT PLUS (consejo) y con tu último estudio de stock (margen y stock parado).</span>
          </div>
        </form>
      )}
    </div>
  );
}

function FarmaciaVsPara({ t, h }) {
  if (t.facturacion_parafarmacia == null) return null;
  const pPara = (t.pct_parafarmacia || 0) * 100;
  return (
    <div className="card p-6 mb-6" data-testid="farmacia-vs-parafarmacia">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Mix de venta</div>
          <h3 className="font-display text-xl font-bold">Farmacia vs parafarmacia</h3>
        </div>
        <span className="text-xs text-slate-500">Parafarmacia = CN por debajo de 400000</span>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div data-testid="kpi-farmacia">
          <div className="text-xs text-slate-500">Farmacia</div>
          <div className="text-2xl font-bold font-display">{formatMoney(t.facturacion_farmacia)}</div>
          <div className="text-xs text-slate-500">{formatPct(1 - (t.pct_parafarmacia || 0))} del total</div>
          {h("facturacion_farmacia", formatMoney, { className: "mt-1" })}
        </div>
        <div className="text-right flex flex-col items-end" data-testid="kpi-parafarmacia">
          <div className="text-xs text-slate-500">Parafarmacia</div>
          <div className="text-2xl font-bold font-display text-violet-700">{formatMoney(t.facturacion_parafarmacia)}</div>
          <div className="text-xs text-slate-500">{formatPct(t.pct_parafarmacia)} del total</div>
          {h("facturacion_parafarmacia", formatMoney, { className: "mt-1" })}
          {h("pct_parafarmacia", formatPct, { pp: true })}
        </div>
      </div>
      <div className="h-3 rounded-full bg-slate-900 overflow-hidden flex justify-end" data-testid="farmacia-para-bar">
        <div className="h-full bg-violet-500 transition-[width] duration-700" style={{ width: `${pPara}%` }} />
      </div>
    </div>
  );
}

const sum = (rows, k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
const wavg = (rows, k, w) => {
  const tw = sum(rows, w);
  return tw ? rows.reduce((a, r) => a + (r[k] || 0) * (r[w] || 0), 0) / tw : 0;
};
const r2 = (v) => Math.round(v * 100) / 100;

// Media por empleado en importes y recuentos; ratios ponderados de toda la farmacia.
export function mediaFarmacia(rows) {
  const n = rows.length;
  const avg = (k) => r2(sum(rows, k) / n);
  const fact = sum(rows, "facturacion");
  const tickets = sum(rows, "tickets");
  const opsReceta = sum(rows, "operaciones_receta");
  return {
    vendedor: "Media farmacia",
    facturacion: avg("facturacion"), facturacion_libre: avg("facturacion_libre"), facturacion_receta: avg("facturacion_receta"),
    facturacion_farmacia: avg("facturacion_farmacia"), facturacion_parafarmacia: avg("facturacion_parafarmacia"),
    pct_parafarmacia: fact ? sum(rows, "facturacion_parafarmacia") / fact : 0,
    tickets: Math.round(tickets / n),
    ticket_medio: tickets ? r2(fact / tickets) : 0,
    ticket_medio_solo_receta: r2(wavg(rows, "ticket_medio_solo_receta", "operaciones_solo_receta")),
    ticket_medio_mixta: r2(wavg(rows, "ticket_medio_mixta", "operaciones_mixtas")),
    ticket_medio_solo_libre: r2(wavg(rows, "ticket_medio_solo_libre", "operaciones_solo_libre")),
    lineas_por_ticket: r2(wavg(rows, "lineas_por_ticket", "tickets")),
    uds_por_ticket: r2(wavg(rows, "uds_por_ticket", "tickets")),
    consejo_eur: avg("consejo_eur"),
    operaciones_mixtas: Math.round(sum(rows, "operaciones_mixtas") / n),
    operaciones_receta: Math.round(opsReceta / n),
    pct_consejo_en_receta: opsReceta ? sum(rows, "operaciones_mixtas") / opsReceta : 0,
    operaciones_consejo: Math.round(sum(rows, "operaciones_consejo") / n),
    ratio_consejo_para: opsReceta ? sum(rows, "operaciones_consejo") / opsReceta : 0,
    ratio_productos_libre_receta: Math.round(wavg(rows, "ratio_productos_libre_receta", "operaciones_receta") * 1000) / 1000,
    margen_eur: rows.some((r) => r.margen_eur != null) ? avg("margen_eur") : null,
    stock_parado_eur: avg("stock_parado_eur"),
    dias: Math.round(sum(rows, "dias") / n),
  };
}

export function EmployeeReportButton({ importId, vendedor }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/sales/imports/${importId}/employee-report`, { params: { vendedor }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe_${vendedor}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo generar el informe");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={download} disabled={busy} title={`Informe PDF de ${vendedor}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-900 hover:text-white transition-colors text-[11px] font-semibold"
            data-testid={`employee-report-${vendedor}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} PDF
    </button>
  );
}

export function EmployeeTable({ rows, hasTickets, hist, importId, names = {}, title = "Indicadores por empleado", refLabel, testid = "employees-panel" }) {
  const sort = useSortable(rows || [], "facturacion", "desc");
  if (!rows?.length) return null;
  const media = mediaFarmacia(rows);
  const cols = [
    { header: "Empleado", key: "vendedor" },
    { header: "Facturación (€)", key: "facturacion", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Operaciones", key: "tickets", align: "right" },
    { header: "Venta libre (€)", key: "facturacion_libre", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Receta (€)", key: "facturacion_receta", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Farmacia (€)", key: "facturacion_farmacia", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Parafarmacia (€)", key: "facturacion_parafarmacia", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "% parafarmacia", key: "pct_parafarmacia", format: (v) => ((v || 0) * 100).toFixed(1), align: "right" },
    { header: "Ticket medio (€)", key: "ticket_medio", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "TM solo receta (€)", key: "ticket_medio_solo_receta", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "TM venta mixta (€)", key: "ticket_medio_mixta", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "TM solo libre (€)", key: "ticket_medio_solo_libre", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Líneas/ticket", key: "lineas_por_ticket", align: "right" },
    { header: "Consejo (€)", key: "consejo_eur", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Recetas con consejo", key: "operaciones_consejo", align: "right" },
    { header: "Operaciones con receta", key: "operaciones_receta", align: "right" },
    { header: "Ratio de consejo (%)", key: "ratio_consejo_para", format: (v) => ((v || 0) * 100).toFixed(1), align: "right" },
    { header: "Ratio ventas mixtas (%)", key: "pct_consejo_en_receta", format: (v) => ((v || 0) * 100).toFixed(1), align: "right" },
    { header: "Productos libres por receta", key: "ratio_productos_libre_receta", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Margen (€)", key: "margen_eur", format: (v) => (v == null ? "" : v.toFixed(2)), align: "right" },
    { header: "Stock parado (€)", key: "stock_parado_eur", format: (v) => (v || 0).toFixed(2), align: "right" },
  ];
  return (
    <HistLabel.Provider value={refLabel || "media 12 m"}>
    <div className="card p-6 mb-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Equipo</div>
          <h3 className="font-display text-xl font-bold">{title}</h3>
        </div>
        <ExportButtons filename="ventas-por-empleado" title="Indicadores por empleado" sheetName="Empleados"
                       orientation="landscape" columns={cols} rows={[...sort.sortedRows, media]} testIdPrefix="export-employees" />
      </div>
      <div className="overflow-x-auto">
        <table className="dense" data-testid="employees-table">
          <thead>
            <tr>
              <th><SortHeader label="Empleado" k="vendedor" sort={sort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Facturación" k="facturacion" sort={sort} num /></th>
              <th className="num"><SortHeader label="Operac." k="tickets" sort={sort} num /></th>
              <th className="num"><SortHeader label="Venta libre" k="facturacion_libre" sort={sort} num /></th>
              <th className="num"><SortHeader label="Receta" k="facturacion_receta" sort={sort} num /></th>
              <th className="num"><SortHeader label="Farmacia" k="facturacion_farmacia" sort={sort} num /></th>
              <th className="num"><SortHeader label="Parafarm." k="facturacion_parafarmacia" sort={sort} num /></th>
              <th className="num"><SortHeader label="% parafarm." k="pct_parafarmacia" sort={sort} num /></th>
              <th className="num"><SortHeader label="Ticket medio" k="ticket_medio" sort={sort} num /></th>
              <th className="num"><SortHeader label="TM solo receta" k="ticket_medio_solo_receta" sort={sort} num /></th>
              <th className="num"><SortHeader label="TM mixta" k="ticket_medio_mixta" sort={sort} num /></th>
              <th className="num"><SortHeader label="TM solo libre" k="ticket_medio_solo_libre" sort={sort} num /></th>
              <th className="num"><SortHeader label="Líneas/tk" k="lineas_por_ticket" sort={sort} num /></th>
              <th className="num"><SortHeader label="Uds/tk" k="uds_por_ticket" sort={sort} num /></th>
              <th className="num"><SortHeader label="Consejo" k="consejo_eur" sort={sort} num /></th>
              <th className="num"><SortHeader label="Rec. c/consejo" k="operaciones_mixtas" sort={sort} num /></th>
              <th className="num"><SortHeader label="Ratio mixtas" k="pct_consejo_en_receta" sort={sort} num /></th>
              <th className="num"><SortHeader label="Prod. libre/receta" k="ratio_productos_libre_receta" sort={sort} num /></th>
              <th className="num"><SortHeader label="Margen" k="margen_eur" sort={sort} num /></th>
              <th className="num"><SortHeader label="Stock parado" k="stock_parado_eur" sort={sort} num /></th>
              <th className="num"><SortHeader label="Días" k="dias" sort={sort} num /></th>
              <th className="num">Informe</th>
            </tr>
          </thead>
          <tbody>
            {sort.sortedRows.map((e) => (
              <tr key={e.vendedor} data-testid={`employee-row-${e.vendedor}`}>
                <EmployeeCells e={e} name={names[e.vendedor]} hasTickets={hasTickets} hist={(hist?.by_employee?.[e.vendedor]?.n_meses || 0) > 1 ? hist.by_employee[e.vendedor] : null} />
                <td className="num"><EmployeeReportButton importId={importId} vendedor={e.vendedor} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 border-t-2 border-slate-900" data-testid="employee-row-media">
              <EmployeeCells e={media} hasTickets={hasTickets} hist={hist?.media_empleado} />
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
    </HistLabel.Provider>
  );
}

function EmployeeCells({ e, hasTickets, hist, name }) {
  const tk = (v) => (hasTickets ? v : "—");
  const r = (k, fmt, pp) => (hist ? <Hist cur={e[k]} hist={hist[k]} fmt={fmt} pp={pp} /> : null);
  const rt = (k, fmt, pp) => (hasTickets ? r(k, fmt, pp) : null);
  return (
    <>
                <td className="font-semibold">{name || e.vendedor}{e.vendedor_cod ? <span className="text-slate-400 font-normal"> ·{e.vendedor_cod}</span> : null}</td>
                <td className="num font-semibold">{formatMoney2(e.facturacion)}{r("facturacion", formatMoney)}</td>
                <td className="num" data-testid={`employee-ops-${e.vendedor}`}>{tk(formatNumber(e.tickets))}{rt("tickets", numInt)}</td>
                <td className="num">{formatMoney2(e.facturacion_libre)}{r("facturacion_libre", formatMoney)}</td>
                <td className="num text-slate-500">{formatMoney2(e.facturacion_receta)}{r("facturacion_receta", formatMoney)}</td>
                <td className="num" data-testid={`employee-farmacia-${e.vendedor}`}>{formatMoney2(e.facturacion_farmacia)}{r("facturacion_farmacia", formatMoney)}</td>
                <td className="num text-violet-700" data-testid={`employee-parafarmacia-${e.vendedor}`}>{formatMoney2(e.facturacion_parafarmacia)}{r("facturacion_parafarmacia", formatMoney)}</td>
                <td className="num font-semibold">{formatPct(e.pct_parafarmacia)}{r("pct_parafarmacia", formatPct, true)}</td>
                <td className="num">{tk(formatMoney2(e.ticket_medio))}{rt("ticket_medio", formatMoney2)}</td>
                <td className="num text-slate-500">{tk(formatMoney2(e.ticket_medio_solo_receta))}{rt("ticket_medio_solo_receta", formatMoney2)}</td>
                <td className="num text-emerald-700 font-semibold">{tk(formatMoney2(e.ticket_medio_mixta))}{rt("ticket_medio_mixta", formatMoney2)}</td>
                <td className="num text-slate-500">{tk(formatMoney2(e.ticket_medio_solo_libre))}{rt("ticket_medio_solo_libre", formatMoney2)}</td>
                <td className="num">{tk(num1(e.lineas_por_ticket))}{rt("lineas_por_ticket", num1)}</td>
                <td className="num">{tk(num1(e.uds_por_ticket))}{rt("uds_por_ticket", num1)}</td>
                <td className="num">{formatMoney2(e.consejo_eur)}{r("consejo_eur", formatMoney)}</td>
                <td className="num">{hasTickets ? `${formatNumber(e.operaciones_mixtas)}/${formatNumber(e.operaciones_receta)}` : "—"}{rt("operaciones_mixtas", numInt)}</td>
                <td className="num font-semibold">{hasTickets ? formatPct(e.pct_consejo_en_receta) : "—"}{rt("pct_consejo_en_receta", formatPct, true)}</td>
                <td className="num">{hasTickets ? (e.ratio_productos_libre_receta ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2 }) : "—"}{rt("ratio_productos_libre_receta", ratio2)}</td>
                <td className="num">{e.margen_eur == null ? "—" : formatMoney2(e.margen_eur)}{e.margen_eur == null ? null : r("margen_eur", formatMoney)}</td>
                <td className="num text-amber-700">{formatMoney2(e.stock_parado_eur)}{r("stock_parado_eur", formatMoney)}</td>
                <td className="num text-slate-500">{e.dias || "—"}{r("dias", numInt)}</td>
    </>
  );
}

const fechaES = (v) => (v ? new Date(v).toLocaleDateString("es-ES") : "");

const yearOf = (im) => (im.date_from || "").slice(0, 4) || (String(im.label || "").match(/\d{4}/) || ["Otros"])[0];

function ImportsSelector({ imports, selected, setSelected }) {
  const years = [...new Set(imports.map(yearOf))].sort().reverse();
  const selYear = yearOf(imports.find((i) => i.id === selected) || imports[0]);
  const [year, setYear] = useState(selYear);
  useEffect(() => { setYear(selYear); }, [selYear]);
  const pick = (y) => { setYear(y); const first = imports.find((i) => yearOf(i) === y); if (first) setSelected(first.id); };
  return (
    <div className="mb-6" data-testid="imports-selector">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {years.map((y) => (
          <button key={y} onClick={() => pick(y)} data-testid={`import-year-${y}`}
                  className={"px-4 py-1.5 rounded-full text-sm font-bold border transition-colors " +
                    (year === y ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400")}>
            {y} <span className="ml-1 text-xs opacity-70">({imports.filter((i) => yearOf(i) === y).length})</span>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {imports.filter((i) => yearOf(i) === year).map((im) => (
          <button key={im.id} onClick={() => setSelected(im.id)}
                  className={"px-3 py-2 rounded-lg text-sm font-semibold border transition-colors " +
                    (selected === im.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400")}
                  data-testid={`import-tab-${im.id}`}>
            {im.label}
            <span className={"ml-2 text-[10px] uppercase tracking-wider " + (selected === im.id ? "text-slate-300" : "text-slate-400")}>
              {KIND_LABEL[im.kind] || im.kind}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function DeadStockPanel({ importId, byEmployee }) {
  const [items, setItems] = useState(null);
  const [hover, setHover] = useState(null);
  useEffect(() => {
    if (!importId) return;
    (async () => {
      try {
        const { data } = await api.get(`/sales/imports/${importId}/dead-stock`);
        setItems(data.items || []);
      } catch { setItems([]); }
    })();
  }, [importId]);
  if (!items?.length) return null;
  const cols = [
    { header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
    { header: "Clase", key: "abcd" },
    { header: "Uds", key: "uds", align: "right" },
    { header: "Facturación (€)", key: "facturacion", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Stock actual", key: "stock", align: "right" },
    { header: "Última entrada", key: "ultima_entrada", format: fechaES },
  ];
  return (
    <div className="card p-6 mb-6" data-testid="dead-stock-panel">
      {hover && (
        <div data-testid="dead-stock-tooltip" className="fixed z-50 pointer-events-none rounded-md bg-slate-900 text-white text-xs px-3 py-2 shadow-lg"
             style={{ left: hover.x + 14, top: hover.y + 14 }}>
          <div>Última entrada: <b>{fechaES(hover.p.ultima_entrada) || "—"}</b></div>
          <div>Stock que queda: <b>{hover.p.stock ?? "—"} uds</b></div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Oportunidad</div>
          <h3 className="font-display text-xl font-bold">Stock parado que SÍ se ha vendido</h3>
          <p className="text-xs text-slate-500 mt-1">Productos clasificados D en tu estudio de stock que han tenido salida este periodo.</p>
        </div>
        <ExportButtons filename="stock-parado-vendido" title="Stock parado vendido" sheetName="StockParado"
                       columns={cols} rows={items} testIdPrefix="export-deadstock" />
      </div>
      <RankingParadoEmpleados rows={byEmployee} />
      <div className="overflow-x-auto max-h-96">
        <table className="dense" data-testid="dead-stock-table">
          <thead><tr><th>CN</th><th>Descripción</th><th>Clase</th><th className="num">Uds</th><th className="num">Facturación</th></tr></thead>
          <tbody>
            {items.slice(0, 100).map((p) => (
              <tr key={p.cn} data-testid={`dead-stock-row-${p.cn}`} className="cursor-help"
                  onMouseMove={(e) => setHover({ p, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setHover(null)}>
                <td className="font-mono">{p.cn}</td>
                <td>{p.descripcion}</td>
                <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
                <td className="num">{formatNumber(p.uds)}</td>
                <td className="num font-semibold">{formatMoney2(p.facturacion)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TicketMedioChart({ data, xKey, kicker, title, subtitle, testid }) {
  if (!data?.length) return null;
  return (
    <div className="card p-6 mb-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        <Clock size={12} /> {kicker}
      </div>
      <h3 className="font-display text-xl font-bold">{title}</h3>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} unit="€" />
          <Tooltip formatter={(v, n) => (n === "Operaciones" ? formatNumber(v) : formatMoney2(v))} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="l" dataKey="tickets" name="Operaciones" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
          <Line yAxisId="r" type="monotone" dataKey="tm_solo_receta" name="TM solo receta" stroke="#0f172a" strokeWidth={2} dot={false} />
          <Line yAxisId="r" type="monotone" dataKey="tm_mixta" name="TM venta mixta" stroke="#10b981" strokeWidth={2.5} dot={false} />
          <Line yAxisId="r" type="monotone" dataKey="tm_solo_libre" name="TM solo libre" stroke="#f59e0b" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function WeekdayTable({ rows, hist }) {
  if (!rows?.length) return null;
  const hw = (d, k, fmt, pp) => (hist?.[d.dow] ? <Hist cur={d[k]} hist={hist[d.dow][k]} fmt={fmt} pp={pp} /> : null);
  const cols = [
    { header: "Día", key: "dia" },
    { header: "Operaciones", key: "tickets", align: "right" },
    { header: "Ticket medio (€)", key: "ticket_medio", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "TM solo receta (€)", key: "tm_solo_receta", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "TM venta mixta (€)", key: "tm_mixta", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "TM solo libre (€)", key: "tm_solo_libre", format: (v) => (v || 0).toFixed(2), align: "right" },
    { header: "Ratio mixtas (%)", key: "pct_mixtas", format: (v) => ((v || 0) * 100).toFixed(1), align: "right" },
  ];
  return (
    <div className="card p-6 mb-6" data-testid="weekday-table-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Detalle semanal</div>
          <h3 className="font-display text-xl font-bold">Ticket medio por día de la semana</h3>
        </div>
        <ExportButtons filename="ticket-medio-por-dia" title="Ticket medio por día de la semana" sheetName="DiasSemana"
                       columns={cols} rows={rows} testIdPrefix="export-weekday" />
      </div>
      <div className="overflow-x-auto">
        <table className="dense" data-testid="weekday-table">
          <thead>
            <tr><th>Día</th><th className="num">Operac.</th><th className="num">Ticket medio</th>
              <th className="num">TM solo receta</th><th className="num">TM mixta</th>
              <th className="num">TM solo libre</th><th className="num">Ratio mixtas</th></tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.dow} data-testid={`weekday-row-${d.dow}`}>
                <td className="font-semibold">{d.dia}</td>
                <td className="num">{formatNumber(d.tickets)}{hw(d, "tickets", numInt)}</td>
                <td className="num font-semibold">{formatMoney2(d.ticket_medio)}{hw(d, "ticket_medio", formatMoney2)}</td>
                <td className="num text-slate-500">{formatMoney2(d.tm_solo_receta)}{hw(d, "tm_solo_receta", formatMoney2)}</td>
                <td className="num text-emerald-700 font-semibold">{formatMoney2(d.tm_mixta)}{hw(d, "tm_mixta", formatMoney2)}</td>
                <td className="num text-slate-500">{formatMoney2(d.tm_solo_libre)}{hw(d, "tm_solo_libre", formatMoney2)}</td>
                <td className="num">{formatPct(d.pct_mixtas)}{hw(d, "pct_mixtas", formatPct, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Ventas() {
  const [imports, setImports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = async (preferId) => {
    const { data } = await api.get("/sales/imports");
    setImports(data);
    const pick = preferId || selected || data[0]?.id || null;
    setSelected(pick);
    const ev = await api.get("/sales/evolution");
    setEvolution(ev.data);
    api.get("/sales/history").then((r) => setHistory(r.data)).catch(() => setHistory(null));
    return pick;
  };

  useEffect(() => {
    (async () => { await load(); setLoading(false); })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let ignore = false;
    setConfirmDelete(false);
    (async () => {
      try {
        const { data } = await api.get(`/sales/imports/${selected}`);
        if (!ignore) { setDetail(data); setErr(""); }
      } catch (e) {
        if (!ignore) setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudieron cargar los datos de esta subida");
      }
    })();
    return () => { ignore = true; };
  }, [selected]);

  const remove = async (id) => {
    try {
      await api.delete(`/sales/imports/${id}`);
      setConfirmDelete(false);
      setSelected(null);
      const rest = await api.get("/sales/imports");
      setImports(rest.data);
      setSelected(rest.data[0]?.id || null);
      const ev = await api.get("/sales/evolution");
      setEvolution(ev.data);
      api.get("/sales/history").then((r) => setHistory(r.data)).catch(() => setHistory(null));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar la subida");
    }
  };

  const t = detail?.totals || {};
  const H = history?.n_meses > 1 && (t.tickets || 0) >= 20 ? history : null;
  const h = (k, fmt, opts = {}) => (H ? <Hist cur={t[k]} hist={H.totals[k]} fmt={fmt} testid={`hist-${k}`} {...opts} /> : null);
  const dayData = useMemo(() => (detail?.by_day || []).map((d) => ({
    ...d, dia: d.fecha?.slice(8) + "/" + d.fecha?.slice(5, 7),
  })), [detail]);
  const ultimos12 = useMemo(() => evolution.slice(-12), [evolution]);
  const evolutionTM = useMemo(() => ultimos12.map((e) => ({
    ...e,
    tm_solo_receta: e.ticket_medio_solo_receta,
    tm_mixta: e.ticket_medio_mixta,
    tm_solo_libre: e.ticket_medio_solo_libre,
  })), [ultimos12]);

  if (loading) return <div className="p-8 flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>;

  return (
    <div className="p-8 fade-in" data-testid="ventas-page">
      <BannerFarmanaliza compact image="/img/farmanaliza-ventas.jpg?v=2" kicker="Ventas mensuales" subtitle="Evolución de ventas"
                         text="Cómo evolucionan tus ventas mes a mes, por empleado, consejo y productos estrella." testid="banner-ventas" />
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Indicadores comerciales</div>
          <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight">Ventas mensuales</h1>
          <p className="text-slate-600 mt-1 text-sm">Sube cada mes el diario de ventas y mide el rendimiento comercial del equipo.</p>
        </div>
        <Link to="/empleados" className="btn btn-outline shrink-0 whitespace-nowrap" data-testid="go-empleados">
          <Target size={15} /> Empleados →
        </Link>
      </div>

      <UploadCard onDone={async (id) => { const p = await load(id); setSelected(p); }} />

      {imports.length === 0 ? (
        <div className="card p-10 text-center text-slate-500" data-testid="no-sales-imports">
          <BarChart3 size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 font-semibold">Todavía no has subido ninguna venta</p>
          <p className="text-sm">Sube el diario de ventas del mes para ver los indicadores comerciales.</p>
        </div>
      ) : (
        <>
          <ImportsSelector imports={imports} selected={selected} setSelected={setSelected} />

          {err && <div className="card mb-6 border border-red-300 bg-red-50 px-5 py-3 text-sm text-red-700" data-testid="ventas-error">{err}</div>}

          {detail && (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-slate-600">
                  {detail.date_from ? <>Periodo: <span className="font-mono font-semibold">{detail.date_from} → {detail.date_to}</span> · </> : null}
                  {formatNumber(t.lineas)} líneas · {formatNumber(t.productos_distintos)} productos distintos
                </div>
                {!confirmDelete ? (
                  <button onClick={() => setConfirmDelete(true)} className="text-xs text-red-700 hover:underline flex items-center gap-1" data-testid="delete-import">
                    <Trash2 size={12} /> Eliminar esta subida
                  </button>
                ) : (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-bold text-red-700">¿Seguro? Se borrarán {formatNumber(t.lineas)} líneas de venta</span>
                    <button onClick={() => remove(detail.id)} className="px-2 py-1 rounded bg-red-600 text-white font-bold" data-testid="delete-import-confirm">Sí, borrar</button>
                    <button onClick={() => setConfirmDelete(false)} className="px-2 py-1 rounded bg-slate-200 font-semibold" data-testid="delete-import-cancel">Cancelar</button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Kpi label="Facturación total" value={formatMoney(t.facturacion)} sub={`${formatNumber(t.unidades)} unidades`} icon={TrendingUp} testid="kpi-facturacion" hist={h("facturacion", formatMoney)} />
                <Kpi label="Venta libre" value={formatMoney(t.facturacion_libre)} sub={`${formatPct(t.pct_libre)} del total`} icon={Receipt} testid="kpi-libre" hist={h("facturacion_libre", formatMoney)} />
                <Kpi label="Receta" value={formatMoney(t.facturacion_receta)} sub={`${formatMoney(t.aportacion_paciente)} aportación paciente`} icon={Pill} testid="kpi-receta" hist={h("facturacion_receta", formatMoney)} />
                <Kpi label="Ticket medio" value={t.tickets ? formatMoney2(t.ticket_medio) : "—"}
                     sub={t.tickets ? `${formatNumber(t.tickets)} operaciones · ${t.lineas_por_ticket} líneas/op`
                                    : (t.operaciones_producto ? `${formatNumber(t.operaciones_producto)} operaciones por producto (sin ticket)` : "el fichero no trae operaciones")}
                     icon={CreditCard} testid="kpi-ticket-medio" hist={t.tickets ? h("ticket_medio", formatMoney2) : null} />
              </div>

              <FarmaciaVsPara t={t} h={h} />

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Kpi label="Venta de consejo" value={formatMoney(t.consejo_eur)} sub="parafarmacia añadida a una receta" icon={Sparkles} testid="kpi-consejo" hist={h("consejo_eur", formatMoney)} />
                <MargenCard importId={detail.id} margen={t.margen_eur} hist={t.margen_eur == null ? null : h("margen_eur", formatMoney)} />
                <Kpi label="Stock D vendido" value={formatMoney(t.stock_parado_eur)} sub={`${formatNumber(t.stock_parado_uds)} uds de clase D`} icon={Package} testid="kpi-stock-parado" hist={h("stock_parado_eur", formatMoney)} />
                <Kpi label="Descuentos concedidos" value={formatMoney2(t.descuento)} sub={t.facturacion_libre ? "sobre venta libre" : "sobre el total"} icon={Target} testid="kpi-descuento" hist={h("descuento", formatMoney2, { inverse: true })} />
              </div>

              {t.operaciones > 0 && (
                <div className="card p-6 mb-6" data-testid="operaciones-panel">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Análisis de la operación completa</div>
                  <h3 className="font-display text-xl font-bold mb-1">Tipo de operación y ticket medio</h3>
                  <p className="text-xs text-slate-500 mb-4">
                    {formatNumber(t.documentos)} documentos agrupados en <b>{formatNumber(t.operaciones)} operaciones reales</b> (misma fecha, hora y vendedor: farmacia + SL cuentan como una sola venta).
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                    <div className="rounded-xl border border-slate-200 p-4" data-testid="op-solo-receta">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Solo receta</div>
                      <div className="font-display text-2xl font-extrabold">{formatNumber(t.operaciones_solo_receta)}</div>
                      <div className="text-xs text-slate-500">ticket medio <b className="text-slate-900">{formatMoney2(t.ticket_medio_solo_receta)}</b></div>
                      {h("operaciones_solo_receta", numInt, { className: "mt-1" })}
                      {h("ticket_medio_solo_receta", formatMoney2)}
                    </div>
                    <div className="rounded-xl border-2 border-emerald-400 bg-emerald-50/50 p-4" data-testid="op-mixta">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Venta mixta (receta + libre)</div>
                      <div className="font-display text-2xl font-extrabold text-emerald-700">{formatNumber(t.operaciones_mixtas)}</div>
                      <div className="text-xs text-emerald-800">ticket medio <b>{formatMoney2(t.ticket_medio_mixta)}</b></div>
                      {h("operaciones_mixtas", numInt, { className: "mt-1" })}
                      {h("ticket_medio_mixta", formatMoney2)}
                    </div>
                    <div className="rounded-xl border border-slate-200 p-4" data-testid="op-solo-libre">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Solo venta libre</div>
                      <div className="font-display text-2xl font-extrabold">{formatNumber(t.operaciones_solo_libre)}</div>
                      <div className="text-xs text-slate-500">ticket medio <b className="text-slate-900">{formatMoney2(t.ticket_medio_solo_libre)}</b></div>
                      {h("operaciones_solo_libre", numInt, { className: "mt-1" })}
                      {h("ticket_medio_solo_libre", formatMoney2)}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 border-t border-slate-100 pt-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Operaciones con receta</div>
                      <div className="font-display text-2xl font-extrabold" data-testid="ops-receta">{formatNumber(t.operaciones_receta)}</div>
                      <div className="text-xs text-slate-500">solo receta + mixtas</div>
                      {h("operaciones_receta", numInt, { className: "mt-1" })}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ratio de ventas mixtas</div>
                      <div className="font-display text-2xl font-extrabold text-emerald-600" data-testid="ops-tasa">{formatPct(t.pct_consejo_en_receta)}</div>
                      <div className="text-xs text-slate-500">{formatNumber(t.operaciones_mixtas)} de {formatNumber(t.operaciones_receta)} operaciones con receta</div>
                      {h("pct_consejo_en_receta", formatPct, { pp: true, className: "mt-1" })}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Productos libres por receta</div>
                      <div className="font-display text-2xl font-extrabold" data-testid="ops-ratio-productos">{(t.ratio_productos_libre_receta ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })}</div>
                      <div className="text-xs text-slate-500">{formatNumber(t.productos_libre_en_receta)} productos de venta libre / {formatNumber(t.operaciones_receta)} recetas</div>
                      {h("ratio_productos_libre_receta", ratio2, { className: "mt-1" })}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ratio de consejo</div>
                      <div className="font-display text-2xl font-extrabold text-emerald-600" data-testid="ops-consejo">{formatPct(t.ratio_consejo_para)}</div>
                      <div className="text-xs text-slate-500">{formatNumber(t.operaciones_consejo)} de {formatNumber(t.operaciones_receta)} recetas llevan parafarmacia · {formatMoney(t.consejo_eur)}</div>
                      {h("ratio_consejo_para", formatPct, { pp: true, className: "mt-1" })}
                    </div>
                  </div>
                </div>
              )}

              {evolution.length > 1 && (
                <>
                  <div className="card p-6 mb-6" data-testid="evolution-panel">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Comparativa</div>
                    <h3 className="font-display text-xl font-bold mb-4">Evolución mes a mes</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={ultimos12}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => formatMoney2(v)} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="facturacion" name="Facturación" stroke="#0f172a" strokeWidth={2} />
                        <Line type="monotone" dataKey="facturacion_libre" name="Venta libre" stroke="#10b981" strokeWidth={2} />
                        <Line type="monotone" dataKey="consejo_eur" name="Consejo" stroke="#f59e0b" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <TicketMedioChart data={evolutionTM} xKey="label" kicker="Comparativa"
                                    title="Ticket medio por tipo, mes a mes"
                                    subtitle="Barras: operaciones del mes. Líneas: ticket medio de cada tipo de operación."
                                    testid="evolution-tm-panel" />
                  <RatiosEvolucion evolution={ultimos12} historico={evolution} />
                  <VentasComparativa evolution={evolution} />
                </>
              )}

              {dayData.length > 1 && (
                <>
                  <div className="card p-6 mb-6" data-testid="by-day-panel">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Detalle diario</div>
                    <h3 className="font-display text-xl font-bold mb-4">Facturación por día</h3>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={dayData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="dia" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => formatMoney2(v)} />
                        <Bar dataKey="facturacion" name="Facturación" fill="#0f172a" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="libre" name="Venta libre" fill="#10b981" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <TicketMedioChart data={dayData} xKey="dia" kicker="Detalle diario"
                                    title="Ticket medio por tipo, día a día"
                                    subtitle="Barras: operaciones del día. Líneas: ticket medio de cada tipo de operación."
                                    testid="by-day-tm-panel" />
                </>
              )}

              {detail.by_employee?.length > 0 && (
                <Link to="/empleados" className="card p-4 mb-6 flex items-center justify-between hover:border-slate-400 transition-colors" data-testid="go-empleados-card">
                  <span className="text-sm"><b>{detail.by_employee.length} empleados</b> en este mes · indicadores, fichas, ranking, incentivos y consejo en la página Empleados</span>
                  <span className="text-sm font-semibold">Ver empleados →</span>
                </Link>
              )}

              {detail.by_hour?.length > 0 && (
                <TicketMedioChart data={detail.by_hour} xKey="hora" kicker="Franja horaria"
                                  title="Ticket medio por hora"
                                  subtitle="Barras: operaciones de esa hora. Líneas: ticket medio de cada tipo de operación."
                                  testid="by-hour-panel" />
              )}

              {detail.by_weekday?.length > 0 && (
                <>
                  <TicketMedioChart data={detail.by_weekday} xKey="dia" kicker="Día de la semana"
                                    title="Ticket medio por día de la semana"
                                    subtitle="Barras: operaciones de ese día. Líneas: ticket medio de cada tipo de operación."
                                    testid="by-weekday-panel" />
                  <WeekdayTable rows={detail.by_weekday} hist={H?.by_weekday} />
                </>
              )}

              <TopAconsejadores importId={detail.id} />

              <ProductosEstrella importId={detail.id} />

              <DeadStockPanel importId={detail.id} byEmployee={detail.by_employee} />

              {detail.top_consejo_receta?.length > 0 && (
                <div className="card p-6 mb-6" data-testid="top-consejo-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Qué funciona</div>
                      <h3 className="font-display text-xl font-bold">Top de productos aconsejados junto a una receta</h3>
                      <p className="text-xs text-slate-500 mt-1">Productos de parafarmacia (CN &lt; 400000) que el equipo añade con más frecuencia dentro de una operación con receta.</p>
                    </div>
                    <ExportButtons filename="top-consejo-receta" title="Top productos aconsejados con receta" sheetName="ConsejoReceta"
                                   columns={[{ header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
                                             { header: "Operaciones", key: "operaciones", align: "right" },
                                             { header: "Uds", key: "uds", align: "right" },
                                             { header: "Facturación (€)", key: "facturacion", format: (v) => (v || 0).toFixed(2), align: "right" }]}
                                   rows={detail.top_consejo_receta} testIdPrefix="export-top-consejo" />
                  </div>
                  <div className="overflow-x-auto max-h-96">
                    <table className="dense" data-testid="top-consejo-table">
                      <thead><tr><th>#</th><th>CN</th><th>Descripción</th><th className="num">Operaciones con receta</th><th className="num">Uds</th><th className="num">Facturación</th></tr></thead>
                      <tbody>
                        {detail.top_consejo_receta.map((p, i) => (
                          <tr key={`${p.cn}-${i}`}>
                            <td className="text-slate-400">{i + 1}</td>
                            <td className="font-mono text-xs">{p.cn}</td>
                            <td className="text-xs">{p.descripcion}</td>
                            <td className="num font-semibold text-emerald-700">{formatNumber(p.operaciones)}</td>
                            <td className="num">{formatNumber(p.uds)}</td>
                            <td className="num">{formatMoney2(p.facturacion)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {detail.by_category?.length > 0 && (
                  <div className="card p-6" data-testid="categories-panel">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Consejo · BOT PLUS</div>
                    <h3 className="font-display text-xl font-bold mb-3">Categorías más vendidas</h3>
                    <div className="overflow-x-auto">
                    <table className="dense">
                      <thead><tr><th>Categoría</th><th className="num">Facturación</th></tr></thead>
                      <tbody>
                        {detail.by_category.slice(0, 12).map((c) => (
                          <tr key={c.label}><td className="text-xs">{c.label}</td><td className="num font-semibold">{formatMoney2(c.value)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
                <div className="card p-6" data-testid="top-products-panel">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ranking</div>
                  <h3 className="font-display text-xl font-bold mb-3">Top productos del periodo</h3>
                  <div className="max-h-96 overflow-y-auto overflow-x-auto">
                    <table className="dense">
                      <thead><tr><th>CN</th><th>Descripción</th><th className="num">Uds</th><th className="num">€</th></tr></thead>
                      <tbody>
                        {(detail.top_products || []).slice(0, 25).map((p) => (
                          <tr key={`${p.cn}-${p.descripcion}`}>
                            <td className="font-mono text-xs">{p.cn}</td>
                            <td className="text-xs">{p.descripcion}</td>
                            <td className="num">{formatNumber(p.uds)}</td>
                            <td className="num font-semibold">{formatMoney2(p.facturacion)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
